/**
 * History routing over the site's routes. `_redirects` sends every path to
 * index.html, so a deep link and an in-app navigation resolve identically —
 * which is also why the <head> is written here, per render: one document serves
 * every address, so its canonical and OG tags are a routing concern.
 */

import { applyMeta, metaForPath } from "./meta.js";

export interface Route {
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
  /** Ephemeral navigation context (for example a tapped card's immutable bundle pointer). */
  state?: unknown;
}

export type View = (route: Route, mount: HTMLElement) => void | (() => void);

/**
 * A view whose module is fetched on first use — route-level code splitting, so
 * the entry chunk every address downloads does not carry every page.
 *
 * `ready()` resolves once the module has loaded (or failed to); from then on
 * the view renders synchronously like any other. The router awaits `ready()`
 * BEFORE it tears down and clears the current page, so an in-app navigation
 * keeps the old page on screen while the next chunk downloads rather than
 * flashing an empty `#app`. A failed fetch (offline, or a deploy that retired
 * the hashed chunk) renders `failed` and is not memoised: the next visit
 * retries.
 */
export interface LazyView extends View {
  ready(): Promise<void>;
}

export function lazy(load: () => Promise<View>, failed: View): LazyView {
  let loaded: View | undefined;
  let pending: Promise<void> | undefined;
  const view = ((route: Route, target: HTMLElement) => (loaded ?? failed)(route, target)) as LazyView;
  view.ready = () => {
    pending ??= load().then(
      (module) => {
        loaded = module;
      },
      (error: unknown) => {
        console.error(error);
        pending = undefined;
      },
    );
    return pending;
  };
  return view;
}

function isLazy(view: View): view is LazyView {
  return typeof (view as Partial<LazyView>).ready === "function";
}

interface Entry {
  pattern: RegExp;
  patternString: string;
  keys: string[];
  view: View;
}

const entries: Entry[] = [];
let teardown: (() => void) | undefined;
/** The view on screen, and the in-place handler it registered while rendering ({@link handleInPlace}). */
let mountedView: View | undefined;
let inPlace: InPlace | undefined;

/**
 * A mounted view's answer to a new address of its OWN route: true = the view
 * took it in place (the watch page switching a board or a round), so the router
 * neither tears the page down nor scrolls; false = render as usual.
 */
export type InPlace = (route: Route) => boolean;

/**
 * Register the mounted view's {@link InPlace} handler. Called while the view
 * renders; the router drops it when it swaps to another page. A `navigate` and a
 * back/forward (`popstate`) both reach it, so history works the same either way.
 */
export function handleInPlace(handler: InPlace): void {
  inPlace = handler;
}

/**
 * A primary click with no modifier: the page may take it. Anything else — a
 * middle click, cmd/ctrl (new tab), shift (new window), alt (download) — is the
 * browser's.
 */
export function isPlainClick(event: Pick<MouseEvent, "button" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}
let mount: HTMLElement | undefined;
let fallback: View | undefined;

/** `/round/:id` → a regex plus the names of its captures. */
export function route(pattern: string, view: View): void {
  const keys: string[] = [];
  const source = pattern
    .split("/")
    .map((part) => {
      if (!part.startsWith(":")) return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      keys.push(part.slice(1));
      return "([^/]+)";
    })
    .join("/");
  entries.push({ pattern: new RegExp(`^${source}/?$`), patternString: pattern, keys, view });
}

export function notFound(view: View): void {
  fallback = view;
}

/**
 * Which view a path resolves to, and with what params — the matching half of
 * `render()`, without the DOM half.
 *
 * Exported so the route table can be tested: a footer that links to `/privacy`
 * and a router that has no `/privacy` produce a 404 that no unit test would
 * otherwise see, and `_redirects` serves index.html for every path, so the
 * mistake would not even show as a broken link.
 */
export function resolve(path: string): { view: View; params: Record<string, string> } | undefined {
  const normalised = path.replace(/\/+$/, "") || "/";
  for (const entry of entries) {
    const match = entry.pattern.exec(normalised);
    if (!match) continue;
    const params: Record<string, string> = {};
    entry.keys.forEach((key, i) => {
      params[key] = decodeURIComponent(match[i + 1] ?? "");
    });
    return { view: entry.view, params };
  }
  return undefined;
}

/**
 * Return the registered route pattern (e.g. `/round/:id`) matching a path,
 * or undefined if no route matches. Reuses the router's own pattern table.
 */
export function matchPattern(path: string): string | undefined {
  const normalised = path.split("?")[0]?.split("#")[0]?.replace(/\/+$/, "") || "/";
  for (const entry of entries) {
    if (entry.pattern.test(normalised)) {
      return entry.patternString;
    }
  }
  return undefined;
}

/** Drop every registered route. Test-only: module state outlives a test file. */
export function resetRoutes(): void {
  entries.length = 0;
  fallback = undefined;
}

export function navigate(to: string, replace = false, state: unknown = null): void {
  if (replace) history.replaceState(state, "", to);
  else history.pushState(state, "", to);
  render();
}

export function start(target: HTMLElement): void {
  mount = target;
  addEventListener("popstate", render);
  // One delegated listener beats one per link: pages re-render constantly.
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || !isPlainClick(event)) return;
    const anchor = (event.target as Element | null)?.closest?.("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href || !href.startsWith("/") || anchor.hasAttribute("target")) return;
    event.preventDefault();
    navigate(href);
  });
  render();
}

/** Bumped per render, so a lazy page that finishes loading after a later navigation is dropped. */
let generation = 0;

function render(): void {
  if (!mount) return;
  const current = ++generation;
  const url = new URL(location.href);
  const path = url.pathname.replace(/\/+$/, "") || "/";
  applyMeta(metaForPath(path));
  const hit = resolve(path);
  // The same route, still mounted: it may take the address in place (no teardown, no refetch).
  if (hit && hit.view === mountedView && inPlace?.({ path, params: hit.params, query: url.searchParams, state: history.state }) === true) return;
  if (hit && isLazy(hit.view)) {
    void hit.view.ready().then(() => {
      if (current === generation) swap(hit, path, url);
    });
    return;
  }
  swap(hit, path, url);
}

function swap(hit: ReturnType<typeof resolve>, path: string, url: URL): void {
  if (!mount) return;
  teardown?.();
  teardown = undefined;
  inPlace = undefined;
  mountedView = hit?.view;
  mount.replaceChildren();
  if (hit) {
    scrollTo({ top: 0 });
    teardown = hit.view({ path, params: hit.params, query: url.searchParams, state: history.state }, mount) ?? undefined;
    return;
  }
  teardown = fallback?.({ path, params: {}, query: url.searchParams, state: history.state }, mount) ?? undefined;
}

/** Mark the current nav link, for both the eye and assistive tech. */
export function syncNav(nav: HTMLElement): void {
  const path = location.pathname;
  for (const link of nav.querySelectorAll("a")) {
    const href = link.getAttribute("href") ?? "";
    const active = href === "/" ? path === "/" : path.startsWith(href);
    if (active) link.setAttribute("aria-current", "page");
    else link.removeAttribute("aria-current");
  }
}
